import { useAuthStore } from "@/lib/auth/AuthStore";
import { loadTossPayments } from "@tosspayments/tosspayments-sdk";
import { getBillingCustomerKey } from "src/api/subscription";
import { createUrlWithParams } from "../utils/payUtils";
/**
 * 월구독(빌링) 등록 결제창 호출 훅
 *
 * 일회용 결제의 위젯(usePgBridge/PaymentWidget)과 무관한 1회성 호출이다.
 * - 서버에서 이 유저의 customerKey를 받아온다. (`GET /billing-keys/customer-key`)
 * - `payment.requestBillingAuth()`로 토스 빌링 등록창(카드/계좌)으로 리다이렉트한다.
 * - 등록 완료 시 successUrl로 `authKey`/`customerKey`가 붙어 돌아온다. (returnURL 처리는 후속)
 */ export const useMonthlyBilling = ()=>{
    const { user  } = useAuthStore();
    const requestBillingAuth = async (method)=>{
        if (!(user === null || user === void 0 ? void 0 : user.id)) {
            throw new Error("로그인이 필요합니다.");
        }
        // 빌링 전용 clientKey. 값이 없으면 CheckoutView의 isBilling이 false라 이 경로에 도달하지 않는다.
        const clientKey = process.env.NEXT_PUBLIC_TOSS_BILLING_CLIENT_KEY;
        // customerKey는 서버가 소유한 값을 그대로 쓴다. 이어지는 `POST /billing-keys`에서
        // 서버가 같은 값으로 토스에 authKey를 교환하므로, FE가 임의로 만들면 발급이 실패한다.
        // 페이지 진입이 아니라 이 시점에 조회하는 건, 서버가 없으면 생성하는(lazy) 엔드포인트라
        // 실제 등록 의사가 있는 유저에게만 키를 만들기 위해서다.
        const customerKey = await getBillingCustomerKey().then((res)=>res.customerKey).catch((error)=>{
            // 그대로 두면 axios의 'Request failed with status code 500'이 스낵바에 노출된다.
            // 원인은 cause로 남기고 사용자에게는 재시도 가능한 문구를 보여준다.
            throw new Error("결제 정보를 불러오지 못했어요. 잠시 후 다시 시도해 주세요.", {
                cause: error
            });
        });
        const tossPayments = await loadTossPayments(clientKey);
        const payment = tossPayments.payment({
            customerKey
        });
        // SDK 2.5.0부터 TRANSFER(퀵 계좌이체)가 TransferBillingAuthRequest로 정식 지원되어
        // 별도 캐스팅 없이 method를 그대로 전달한다.
        // 단, 자동결제 계좌이체는 별도 심사·계약과 그 상점에 매칭된 clientKey가 필요하다.
        // TODO(월구독): 심사 전이라 TRANSFER는 UI에서 숨긴 상태다. (constants/billing.ts의 BILLING_METHOD_OPTIONS)
        const billingAuthRequest = {
            method,
            // successUrl/failUrl은 현재 checkout URL(id·orderType 유지) + 빌링 결과 파라미터
            successUrl: createUrlWithParams({
                billingResult: "success"
            }),
            failUrl: createUrlWithParams({
                billingResult: "fail"
            }),
            customerEmail: user.email,
            customerName: user.displayName
        };
        await payment.requestBillingAuth(billingAuthRequest);
    };
    return {
        requestBillingAuth
    };
};
